/**
 * ActivityItem molecule — single activity timeline entry.
 *
 * Leading lucide icon chosen by event type:
 *   comment_added    -> MessageSquare
 *   status_changed   -> ArrowRight
 *   profile_edited   -> Pencil
 *   assigned_to_job  -> Briefcase
 *   cv_ingested      -> FileText (fallback)
 *
 * Renders: icon + summary copy (body-sm) + timestamp (caption secondary).
 * Uses the event's pre-rendered `summary` field or composes from the
 * talentPool.activity.* interpolation keys.
 *
 * All copy via i18next. No hardcoded strings, no gradients, no emojis.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  MessageSquare,
  ArrowRight,
  Pencil,
  Briefcase,
  FileText,
} from 'lucide-react';
import { Typography } from '@/components/ui/atoms/Typography';
import type { TalentActivityEvent, TalentActivityEventType } from '@/types/talent.types';

export interface ActivityItemProps {
  event: TalentActivityEvent;
}

function getEventIcon(type: TalentActivityEventType): React.ReactNode {
  switch (type) {
    case 'comment_added':
      return <MessageSquare size={16} />;
    case 'status_changed':
      return <ArrowRight size={16} />;
    case 'profile_edited':
      return <Pencil size={16} />;
    case 'assigned_to_job':
      return <Briefcase size={16} />;
    case 'cv_ingested':
    default:
      return <FileText size={16} />;
  }
}

function formatTimestamp(isoString: string): string {
  return new Date(isoString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export const ActivityItem: React.FC<ActivityItemProps> = ({ event }) => {
  const { t } = useTranslation('talentPool');

  // Prefer pre-rendered summary; fall back to composing from i18n keys
  const getSummary = (): string => {
    if (event.summary) return event.summary;

    switch (event.type) {
      case 'comment_added':
        return `${event.actorName} ${t('activity.commentAdded')}`;
      case 'status_changed':
        return `${event.actorName} ${t('activity.statusChanged', {
          jobTitle: event.jobTitle ?? '',
          from: event.fromStatus ?? '',
          to: event.toStatus ?? '',
        })}`;
      case 'profile_edited':
        return `${event.actorName} ${t('activity.profileEdited', {
          section: event.section ?? '',
        })}`;
      case 'assigned_to_job':
        return `${event.actorName} ${t('activity.assignedToJob', {
          jobTitle: event.jobTitle ?? '',
        })}`;
      default:
        return event.summary || '';
    }
  };

  return (
    <div className="flex gap-3 py-2">
      <div className="flex items-start pt-0.5 shrink-0 text-dark-invirtus/60">
        {getEventIcon(event.type)}
      </div>
      <div className="flex-1 min-w-0">
        <Typography variant="body-sm" color="secondary">
          {getSummary()}
        </Typography>
        <Typography variant="caption" color="secondary" className="mt-0.5">
          {formatTimestamp(event.createdAt)}
        </Typography>
      </div>
    </div>
  );
};

ActivityItem.displayName = 'ActivityItem';
